import React, { useState, useEffect } from 'react';
import { Zap, Music, Radio, Disc3, Sparkles, Send, Lock, Unlock, PartyPopper } from 'lucide-react';
import GateCountdown from './GateCountdown';
import './BetaGate.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BetaGate = ({ onAccessGranted }) => {
  const [accessCode, setAccessCode] = useState('');
  const [showRequest, setShowRequest] = useState(false);
  const [requestForm, setRequestForm] = useState({
    name: '',
    email: '',
    reason: '',
    userType: 'dj'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isUnlocking, setIsUnlocking] = useState(false);

  // Particle animation effect
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    // Generate random particles
    const generateParticles = () => {
      const newParticles = [];
      for (let i = 0; i < 50; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 4 + 1,
          duration: Math.random() * 3 + 2,
          delay: Math.random() * 2
        });
      }
      setParticles(newParticles);
    };
    generateParticles();
  }, []);

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    const maxRetries = 3;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);

        const response = await fetch(`${BACKEND_URL}/api/beta/verify-code`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: accessCode.toUpperCase() }),
          signal: controller.signal
        });
        clearTimeout(timeout);

        const data = await response.json();

        if (response.ok && data.valid) {
          setIsUnlocking(true);
          localStorage.setItem('beatseek_beta_access', 'true');
          localStorage.setItem('beatseek_beta_code', accessCode.toUpperCase());
          setTimeout(() => {
            onAccessGranted();
          }, 2000);
        } else {
          setError(data.message || 'Invalid access code. Please try again.');
        }
        setIsSubmitting(false);
        return;
      } catch (err) {
        if (attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 1000 * attempt));
          continue;
        }
        setError('Connection error. Please try again.');
      }
    }
    setIsSubmitting(false);
  };

  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    const maxRetries = 3;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);

        const response = await fetch(`${BACKEND_URL}/api/beta/request-access`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestForm),
          signal: controller.signal
        });
        clearTimeout(timeout);

        const data = await response.json();

        if (response.ok) {
          setSuccess('Request submitted! We\'ll review your application and get back to you soon.');
          setRequestForm({ name: '', email: '', reason: '', userType: 'dj' });
          setTimeout(() => setShowRequest(false), 3000);
        } else {
          setError(data.message || 'Failed to submit request. Please try again.');
        }
        setIsSubmitting(false);
        return;
      } catch (err) {
        if (attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 1000 * attempt));
          continue;
        }
        setError('Connection error. Please try again.');
      }
    }
    setIsSubmitting(false);
  };

  return (
    <div className={`beta-gate ${isUnlocking ? 'unlocking' : ''}`}>
      {/* Animated Background */}
      <div className="gate-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="grid-overlay"></div>
        
        {/* Floating Music Notes */}
        <div className="music-notes-container">
          {['♪', '♫', '♬', '♩', '𝄞', '♪', '♫', '♬', '♩', '𝄢', '♪', '♫', '♬', '♩', '♪'].map((note, i) => (
            <div
              key={i}
              className="floating-note"
              style={{
                left: `${5 + (i * 6.5)}%`,
                animationDelay: `${i * 0.6}s`,
                fontSize: `${18 + (Math.sin(i * 1.5) + 1) * 15}px`,
                animationDuration: `${8 + Math.random() * 4}s`
              }}
            >
              {note}
            </div>
          ))}
        </div>
        
        {/* Floating Particles */}
        <div className="particles-container">
          {particles.map(particle => (
            <div
              key={particle.id}
              className="particle"
              style={{
                left: `${particle.x}%`,
                top: `${particle.y}%`,
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                animationDuration: `${particle.duration}s`,
                animationDelay: `${particle.delay}s`
              }}
            />
          ))}
        </div>
        
        {/* Scan Lines */}
        <div className="scan-lines"></div>
        
        {/* Sound Wave Effect */}
        <div className="sound-wave">
          {[...Array(90)].map((_, i) => (
            <div
              key={i}
              className="wave-bar"
              style={{
                animationDelay: `${i * 0.025}s`,
                animationDuration: `${0.6 + Math.sin(i * 0.2) * 0.3}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Unlock Animation Overlay */}
      {isUnlocking && (
        <div className="unlock-overlay">
          <div className="unlock-ring ring-1"></div>
          <div className="unlock-ring ring-2"></div>
          <div className="unlock-ring ring-3"></div>
          <div className="unlock-icon">
            <Unlock size={64} />
          </div>
          <div className="unlock-text">ACCESS GRANTED</div>
          <div className="unlock-subtext">Welcome to BEATSEEK</div>
        </div>
      )}

      {/* Main Content */}
      <div className="gate-content">
        {/* Vinyl Record - Floating independently */}
        <div className="vinyl-record">
          <div className="vinyl-grooves"></div>
          <div className="vinyl-label"></div>
          <div className="vinyl-center"></div>
        </div>
        
        {/* Logo & Title */}
        <div className="gate-header">
          <div className="logo-container">
            <div className="logo-glow"></div>
          </div>
          
          <h1 className="gate-title">
            <span className="title-beat">BEAT</span>
            <span className="title-seek">SEEK</span>
          </h1>
          
          <p className="gate-subtitle">Bringing Electronic Music, DJ Culture & Community Together</p>
          <p className="gate-tagline-small"><em>One Platform · Infinite Vibes</em></p>
          
          <div className="gate-badge">
            <Sparkles size={14} />
            <span>BETA ACCESS</span>
            <Sparkles size={14} />
          </div>

          <GateCountdown />
        </div>

        {/* Feature Highlights - 3 rows */}
        <div className="feature-pills">
          <div className="feature-row">
            <div className="feature-pill"><Music size={14} /><span>AI Track Recognition</span></div>
            <div className="feature-pill"><Radio size={14} /><span>DJ Mixer</span></div>
            <div className="feature-pill"><Zap size={14} /><span>Crate Building</span></div>
            <div className="feature-pill"><Disc3 size={14} /><span>BPM & Key Detection</span></div>
          </div>
          <div className="feature-row">
            <div className="feature-pill"><Sparkles size={14} /><span>Smart Recommendations</span></div>
            <div className="feature-pill"><Music size={14} /><span>Spotify & SoundCloud</span></div>
            <div className="feature-pill"><Send size={14} /><span>Social Hub</span></div>
            <div className="feature-pill"><Music size={14} /><span>Lounge</span></div>
          </div>
          <div className="feature-row">
            <div className="feature-pill"><Radio size={14} /><span>BEATSEEK Radio</span></div>
            <div className="feature-pill"><Sparkles size={14} /><span>Industry News</span></div>
            <div className="feature-pill"><PartyPopper size={14} /><span>Events</span></div>
            <div className="feature-pill"><Zap size={14} /><span>And More...</span></div>
          </div>
        </div>

        {/* Access Form */}
        {!showRequest ? (
          <div className="access-section">
            <form onSubmit={handleCodeSubmit} className="code-form">
              <div className="input-wrapper">
                <Lock size={20} className="input-icon" />
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  placeholder="ENTER ACCESS CODE"
                  maxLength={60}
                  className="code-input"
                  disabled={isSubmitting}
                />
                <button 
                  type="submit" 
                  className="submit-btn"
                  disabled={!accessCode || isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="spinner"></div>
                  ) : (
                    <Zap size={16} />
                  )}
                </button>
              </div>
              {error && <div className="error-message">{error}</div>}
            </form>

            <div className="divider">
              <span>OR</span>
            </div>

            <button 
              className="request-btn"
              onClick={() => setShowRequest(true)}
            >
              <PartyPopper size={18} />
              <span>Request Beta Access</span>
            </button>
          </div>
        ) : (
          <div className="request-section">
            <button 
              className="back-btn"
              onClick={() => setShowRequest(false)}
            >
              ← Back to Code Entry
            </button>
            
            <form onSubmit={handleRequestSubmit} className="request-form">
              <h3 className="form-title">Request Beta Access</h3>
              
              <div className="form-group">
                <label>Your Name</label>
                <input
                  type="text"
                  value={requestForm.name}
                  onChange={(e) => setRequestForm({...requestForm, name: e.target.value})}
                  placeholder="DJ Awesome"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={requestForm.email}
                  onChange={(e) => setRequestForm({...requestForm, email: e.target.value})}
                  placeholder="you@example.com"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>I am a...</label>
                <select
                  value={requestForm.userType}
                  onChange={(e) => setRequestForm({...requestForm, userType: e.target.value})}
                >
                  <option value="dj">DJ</option>
                  <option value="producer">Producer</option>
                  <option value="streamer">Streamer</option>
                  <option value="label">Label / A&R</option>
                  <option value="venue">Venue / Promoter</option>
                  <option value="fan">Music Fan</option>
                  <option value="investor">Investor</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Why do you want to join BEATSEEK?</label>
                <textarea
                  value={requestForm.reason}
                  onChange={(e) => setRequestForm({...requestForm, reason: e.target.value})}
                  placeholder="Tell us about yourself and what excites you about BEATSEEK..."
                  rows={3}
                  required
                />
              </div>
              
              <button 
                type="submit" 
                className="submit-request-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    <span>Submit Request</span>
                  </>
                )}
              </button>
              
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
            </form>
          </div>
        )}

        {/* Footer */}
        <div className="gate-footer">
          <p>🎧 Built for DJs, Producers & Music Lovers</p>
          <div className="social-links">
            <span>Join the movement</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BetaGate;
